<script setup>
defineOptions({ name: "HomeClientsSection" });
import { ref, computed, onMounted, onBeforeUnmount } from "vue";

const props = defineProps({
  // Slug do segmento (ex.: "acougues"). Se informado e existir depoimento
  // marcado com esse segmento em `segments`, o carrossel prioriza esses.
  // Sem correspondência, cai no carrossel genérico de sempre (fallback).
  segment: {
    type: String,
    default: "",
  },
});

const allTestimonials = [
  {
    id: 1,
    content:
      "O suporte técnico é excepcional. Sempre prontos para ajudar e resolver qualquer questão.",
    author: "Osmar Paz",
    role: "Diretor Operacional",
    company: "Friomark Refrigeração",
    // image: "/testimonials/andre-iemar.svg",
    // videoUrl:
    // "https://www.youtube.com/watch?v=sxzlU0Ww03s&list=PLR-N8Tusj5jKxMYb7s_X34NJp7ZY0_cQj&index=2",
    segments: ["fabricas"],
  },
  {
    id: 2,
    content:
      "Antes da Kali Sistemas, trabalhávamos com muitas incertezas e sem confiança nas informações do estoque, o que dificultava a tomada de decisões. Hoje, tenho muito mais segurança, um sistema fácil de usar e que evolui junto com a nossa empresa. Indico a Kali Sistemas, sem sombra de dúvidas, para qualquer empresa que busque organização, confiabilidade e crescimento.",
    author: "Aline Melo",
    role: "Gerente Operacional",
    company: "BioRep Biotecnologias da Reprodução",
    segments: ["agronegocio"],
  },
  {
    id: 3,
    content:
      "Nossa rotina dependia de papéis e planilhas, o que dificultava a organização e o controle da empresa. Hoje temos mais praticidade, acesso rápido às informações e muito mais segurança para gerenciar o financeiro, as vendas e o estoque. Recomendo a Kali Sistemas para empresas que desejam ganhar eficiência, economizar tempo e modernizar sua gestão.",
    author: "Keise Fraga",
    role: "Diretora Operacional",
    company: "Indústrias Fraga",
    // image: "/testimonials/ana-oliveira.svg",
    segments: [],
  },
  {
    id: 4,
    content:
      "Desde que implementamos o Kali Gestão, nossa eficiência operacional melhorou significativamente, proporcionando mais agilidade, organização e controle dos processos. Hoje conseguimos tomar decisões com mais rapidez e segurança, com informações confiáveis em tempo real. Os resultados refletiram diretamente na produtividade, com um ganho de aproximadamente 40% na eficiência operacional.",
    author: "Carlos Mendes",
    role: "Diretor Financeiro",
    company: "Grupo Inovação",
    // image: "/testimonials/carlos-mendes.svg",
    segments: ["varejo"],
  },
  //{
  //  id: 5,
  //  content:
  //    "O suporte técnico é excepcional. Sempre prontos para ajudar e resolver qualquer questão.",
  //  author: "Patricia Lima",
  //  role: "Coordenadora de Operações",
  //  company: "Logística Express",
  //  image: "/testimonials/patricia-lima.svg",
  //  segments: [],
  //},
];

// Sem depoimento real marcado pro segmento pedido, cai no carrossel genérico.
const testimonials = computed(() => {
  if (!props.segment) return allTestimonials;
  const filtered = allTestimonials.filter((t) => t.segments?.includes(props.segment));
  return filtered.length ? filtered : allTestimonials;
});

const currentSlide = ref(0);
const direction = ref("next");
const isTransitioning = ref(false);
const showVideoModal = ref(false);
const currentVideo = ref("");

// Scroll-triggered entry
const sectionRef = ref(null);
const visible = ref(false);

const current = computed(() => testimonials.value[currentSlide.value]);
const hasMedia = computed(() => !!current.value?.image || !!current.value?.videoUrl);
const transitionName = computed(() =>
  direction.value === "next" ? "slide-next" : "slide-prev"
);

// --- Autoplay ---
let timer = null;
const startAutoplay = () => {
  clearInterval(timer);
  timer = setInterval(goNext, 10000);
};
const stopAutoplay = () => clearInterval(timer);

// --- Navigation ---
const goNext = () => {
  if (isTransitioning.value) return;
  direction.value = "next";
  isTransitioning.value = true;
  currentSlide.value = (currentSlide.value + 1) % testimonials.value.length;
  setTimeout(() => { isTransitioning.value = false; }, 800);
};
const goPrev = () => {
  if (isTransitioning.value) return;
  direction.value = "prev";
  isTransitioning.value = true;
  currentSlide.value =
    (currentSlide.value - 1 + testimonials.value.length) % testimonials.value.length;
  setTimeout(() => { isTransitioning.value = false; }, 800);
};
const goTo = (index) => {
  if (isTransitioning.value || index === currentSlide.value) return;
  direction.value = index > currentSlide.value ? "next" : "prev";
  isTransitioning.value = true;
  currentSlide.value = index;
  setTimeout(() => { isTransitioning.value = false; }, 800);
  startAutoplay();
};

// --- Video ---
const openVideo = (url) => {
  currentVideo.value = url;
  showVideoModal.value = true;
  stopAutoplay();
};
const closeVideo = () => {
  showVideoModal.value = false;
  currentVideo.value = "";
  startAutoplay();
};

// --- Lifecycle ---
let observer = null;
onMounted(() => {
  startAutoplay();
  observer = new IntersectionObserver(
    ([entry]) => {
      if (entry.isIntersecting) {
        visible.value = true;
        observer.disconnect();
      }
    },
    { threshold: 0.15 }
  );
  if (sectionRef.value) observer.observe(sectionRef.value);
});
onBeforeUnmount(() => {
  stopAutoplay();
  observer?.disconnect();
});
</script>

<template>
  <section ref="sectionRef" class="relative bg-gradient-to-b from-white to-gray-50 py-12 sm:py-16 overflow-hidden">
    <div class="absolute inset-0 bg-white" aria-hidden="true"></div>

    <div class="mx-auto max-w-7xl px-4 sm:px-6">
      <div class="mx-auto max-w-2xl lg:max-w-none">
        <!-- Header — entra com scroll -->
        <div class="text-center">
          <h2 :class="[
            'text-4xl font-black tracking-tight !leading-[1.25] pb-2 text-transparent bg-clip-text bg-gradient-to-r from-gray-900 via-[#006086] to-[#004d6b] sm:text-5xl',
            'transition-all duration-700 ease-out',
            visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8',
          ]">
            O que nossos clientes dizem
          </h2>
          <p :class="[
            'mt-4 max-w-3xl mx-auto text-lg leading-8 text-gray-600',
            'transition-all duration-700 ease-out delay-150',
            visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8',
          ]">
            Depoimentos de quem já transformou sua gestão com a Kali Sistemas
          </p>
        </div>

        <!-- Carousel — entra com stagger após header -->
        <div :class="[
          'relative mx-auto mt-10 max-w-7xl',
          'transition-all duration-700 ease-out delay-300',
          visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8',
        ]">
          <!-- Navigation Buttons -->
          <div
            class="absolute left-[-4rem] right-[-4rem] md:left-[-6rem] md:right-[-6rem] top-1/2 z-30 hidden md:flex justify-between items-center -mt-4 pointer-events-none">
            <button @click="goPrev"
              class="p-3 rounded-full bg-white shadow-xl hover:bg-gray-50 transition-colors duration-200 focus:outline-none pointer-events-auto"
              aria-label="Depoimento anterior">
              <svg class="w-5 h-5 text-[#006086]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2.5" d="M15 19l-7-7 7-7" />
              </svg>
            </button>
            <button @click="goNext"
              class="p-3 rounded-full bg-white shadow-xl hover:bg-gray-50 transition-colors duration-200 focus:outline-none pointer-events-auto"
              aria-label="Próximo depoimento">
              <svg class="w-5 h-5 text-[#006086]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2.5" d="M9 5l7 7-7 7" />
              </svg>
            </button>
          </div>

          <!-- Testimonial — transição direcional -->
          <div class="relative w-full overflow-hidden" style="min-height: 400px">
            <Transition :name="transitionName">
              <div :key="currentSlide" class="grid gap-8 bg-white rounded-2xl overflow-hidden py-8"
                :class="hasMedia ? 'md:grid-cols-2 pl-0 pr-8' : 'md:grid-cols-1 px-8'" @mouseenter="stopAutoplay"
                @mouseleave="startAutoplay">
                <!-- Imagem / Vídeo -->
                <div v-if="hasMedia" class="relative rounded-2xl overflow-hidden shadow-xl aspect-video w-full"
                  :class="{ 'cursor-pointer group': current.videoUrl }"
                  @click="current.videoUrl && openVideo(current.videoUrl)">
                  <img v-if="current.image" :src="current.image" alt="Depoimento"
                    class="w-full h-full object-cover select-none" />
                  <div v-else class="w-full h-full bg-slate-100"></div>
                  <div v-if="current.videoUrl" class="absolute inset-0 flex items-center justify-center">
                    <div
                      class="relative w-20 h-20 rounded-full flex items-center justify-center group-hover:scale-110 transition-transform duration-300">
                      <div
                        class="absolute inset-0 rounded-full border border-white border-[3px] group-hover:animate-ping">
                      </div>
                      <div
                        class="absolute inset-0 rounded-full border border-white border-[3px] opacity-50 group-hover:animate-ping delay-75">
                      </div>
                      <div class="absolute inset-0 rounded-full border border-white border-[3px]"></div>
                      <div class="relative z-10 ml-1">
                        <svg class="w-12 h-12" viewBox="0 0 24 24" fill="none" stroke="white" stroke-width="2">
                          <polygon points="8 6 18 12 8 18 8 6" fill="white" stroke="white" stroke-width="2"
                            stroke-linecap="round" stroke-linejoin="round" />
                        </svg>
                      </div>
                    </div>
                  </div>
                </div>

                <!-- Texto -->
                <div class="flex flex-col justify-between" :class="[!hasMedia ? 'items-center text-center' : '']">
                  <div :class="[!hasMedia ? 'max-w-3xl' : '']">
                    <img src="@/assets/icon-aspas.svg" alt="" class="w-14 h-14 mb-4"
                      :class="[!hasMedia ? 'mx-auto' : '']" style="color: #006086" />
                    <blockquote class="text-2xl font-light leading-relaxed text-gray-700 mb-6 break-words">
                      {{ current.content }}
                    </blockquote>
                    <div class="text-gray-900">
                      <div class="font-bold">{{ current.author }}</div>
                      <div class="text-[#006086]">
                        {{ current.role }}<br />{{ current.company }}
                      </div>
                    </div>
                  </div>
                  <button v-if="current.videoUrl" @click="openVideo(current.videoUrl)"
                    class="inline-flex items-center gap-2 text-[#006086] font-bold hover:text-white transition-colors duration-200 mt-6 rounded-full px-4 py-2 border border-[#006086] hover:bg-[#006086]">
                    <svg class="w-6 h-6" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M8 5v14l11-7z" />
                    </svg>
                    <span>Confira!</span>
                  </button>
                </div>
              </div>
            </Transition>
          </div>

          <!-- Dot indicators -->
          <div class="flex justify-center gap-2 mt-6">
            <button v-for="(_, i) in testimonials" :key="i" @click="goTo(i)" :aria-label="`Ir para depoimento ${i + 1}`"
              :class="[
                'h-2 rounded-full transition-all duration-300',
                i === currentSlide
                  ? 'bg-kali-blue-500 w-6'
                  : 'bg-gray-300 w-2 hover:bg-gray-400',
              ]" />
          </div>
        </div>
      </div>
    </div>
  </section>

  <!-- Video Modal -->
  <div v-if="showVideoModal" class="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-75"
    @click="closeVideo">
    <div class="relative w-full max-w-4xl mx-4" @click.stop>
      <div class="aspect-video">
        <iframe :src="currentVideo.replace('watch?v=', 'embed/')" class="w-full h-full" frameborder="0"
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
          allowfullscreen></iframe>
      </div>
      <button @click="closeVideo" class="absolute -top-12 right-0 text-white hover:text-gray-300 focus:outline-none">
        <svg class="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
        </svg>
      </button>
    </div>
  </div>
</template>

<style scoped>
/* ─── Transição direcional do carousel ─── */

/* Próximo: entra pela direita, sai pela esquerda */
.slide-next-enter-active {
  animation: slideInFromRight 0.45s cubic-bezier(0.25, 0.46, 0.45, 0.94) both;
}

.slide-next-leave-active {
  animation: slideOutToLeft 0.35s cubic-bezier(0.55, 0, 1, 0.45) both;
  position: absolute;
  inset: 0;
}

/* Anterior: entra pela esquerda, sai pela direita */
.slide-prev-enter-active {
  animation: slideInFromLeft 0.45s cubic-bezier(0.25, 0.46, 0.45, 0.94) both;
}

.slide-prev-leave-active {
  animation: slideOutToRight 0.35s cubic-bezier(0.55, 0, 1, 0.45) both;
  position: absolute;
  inset: 0;
}

@keyframes slideInFromRight {
  from {
    opacity: 0;
    transform: translateX(56px);
  }

  to {
    opacity: 1;
    transform: translateX(0);
  }
}

@keyframes slideOutToLeft {
  from {
    opacity: 1;
    transform: translateX(0);
  }

  to {
    opacity: 0;
    transform: translateX(-56px);
  }
}

@keyframes slideInFromLeft {
  from {
    opacity: 0;
    transform: translateX(-56px);
  }

  to {
    opacity: 1;
    transform: translateX(0);
  }
}

@keyframes slideOutToRight {
  from {
    opacity: 1;
    transform: translateX(0);
  }

  to {
    opacity: 0;
    transform: translateX(56px);
  }
}

/* ─── Animações de outros elementos internos ─── */
@keyframes border-flow {
  0% {
    background-position: 0% 50%;
  }

  100% {
    background-position: 200% 50%;
  }
}

.animate-border-flow {
  animation: border-flow 4s ease-in-out infinite;
  background-size: 200% auto;
}

@keyframes slideLeft {
  from {
    opacity: 0;
    transform: translateX(-100px);
  }

  to {
    opacity: 1;
    transform: translateX(0);
  }
}

@keyframes slideRight {
  from {
    opacity: 0;
    transform: translateX(100px);
  }

  to {
    opacity: 1;
    transform: translateX(0);
  }
}

.animate-slideLeft {
  animation: slideLeft 1s ease-out forwards;
}

.animate-slideRight {
  animation: slideRight 1s ease-out forwards;
}
</style>
